import { createFileRoute } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { SEARCH_TERM_SECTIONS } from "@/lib/search-term-sections";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/foam-roof-repair-phoenix";
const NAME = "Foam Roof Repair";
const TITLE = "Foam Roof Repair Phoenix, AZ | Leaks & Blisters | Durafoam";
const DESCRIPTION =
  "Foam roof repair in Phoenix, AZ — leak diagnosis, blister and bird-peck patching, coating restoration on any SPF roof. Family-owned since 1989. Free inspection.";


const faqs = [
  {
    q: "Can a leaking foam roof be repaired or does it need replacing?",
    a: "The vast majority of foam roof leaks are repairable. SPF is a sacrificial-coating system — once the coating wears through, the foam underneath starts to degrade, but isolated damage can almost always be cut out, refoamed, and recoated. We only recommend full replacement when the substrate or large areas of foam are failing.",
  },
  {
    q: "What causes foam roof damage in Phoenix?",
    a: "The three big causes here are UV breakdown of an overdue coating, bird pecks (especially from grackles and woodpeckers), and impact damage from foot traffic on AC service calls. All three are easy to repair when caught early.",
  },
  {
    q: "How quickly can you respond to a foam roof leak?",
    a: "We aim to be on your roof within 24–48 hours for active leaks during monsoon season, often same-day for existing customers. Call 480-941-5373 — we'll triage immediately.",
  },
  {
    q: "Will repairs blend with my existing roof?",
    a: "Yes. We color-match the coating and feather the patch into the surrounding surface so repairs are nearly invisible from the ground. Function comes first, but appearance matters too.",
  },
  {
    q: "Do you repair foam roofs other companies installed?",
    a: "Absolutely. We repair foam roofs regardless of who put them on. Many of our long-term customers came to us after an installer went out of business or stopped returning calls.",
  },
  {
    q: "How much does foam roof repair cost?",
    a: "Most foam roof repairs in Phoenix run a few hundred to low-thousand dollars — cutting out and refoaming localized damage is a surgical job, not a re-roof. Recoating a whole weathered roof costs more but is still a fraction of replacement. Book a free inspection and we'll hand you an exact written number.",
  },
  {
    q: "How long do foam roof repairs last?",
    a: "A properly done foam repair — cut out, refoamed to matching density, and recoated — lasts as long as the surrounding roof. We've patched foam roofs that are still holding twenty years later. The key is catching damage before UV gets into the foam itself.",
  },
];

export const Route = createFileRoute("/foam-roof-repair-phoenix")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) => serviceHead({ override: loaderData?.seo, slug: SLUG, title: TITLE, description: DESCRIPTION, serviceName: NAME, faqs, rating: loaderData?.rating, reviewCount: loaderData?.reviewCount }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="foam-roof-repair-phoenix"
      extraSections={SEARCH_TERM_SECTIONS["foam-roof-repair-phoenix"]}
      eyebrow="Foam Roof Repair"
      h1="Foam Roof Repair in Phoenix"
      subhead="Fast, surgical repairs for leaking, blistered, or bird-pecked foam roofs across the Valley — done right the first time by people who've been repairing SPF since 1989."
      heroBullets={[
        "Same-week response, faster during monsoon",
        "Leak source diagnosed, not just patched",
        "Color-matched coating repairs",
        "Workmanship warranty on every repair",
      ]}
      whatIs={{
        heading: "How foam roof repair works",
        body: (
          <>
            <p>
              Foam roof repair starts with finding the actual leak — not just the spot you see water
              coming through inside. Water travels along the underside of the deck before it drips,
              which means the failure on the roof is almost never directly above the stain on your
              ceiling. Skip the diagnosis and you patch the wrong spot.
            </p>
            <p>
              Once we locate the failure, the repair sequence is straightforward: we cut out
              compromised foam down to sound material, refoam the void to match surrounding thickness,
              then rebuild the coating in two passes so the patch ties into the rest of the roof.
              The whole repair is monolithic with the existing system — no seams, no patches that
              peel off in three years.
            </p>
            <p>
              For coating-only failures (peeling, chalking, UV breakdown without foam damage) we
              pressure-clean the affected area, apply a primer where needed, and recoat. That
              extends the system for another full coating cycle.
            </p>
          </>
        ),
      }}
      climate={{
        heading: "Phoenix-specific foam roof failures we see",
        body: (
          <>
            <p>
              Foam roofs fail in predictable, Phoenix-specific patterns. Recognizing them early is
              the difference between a $400 patch and a $4,000 section replacement.
            </p>
            <ul>
              <li>
                <strong>Overdue recoat:</strong> Coatings are sacrificial — they're meant to wear so
                the foam doesn't. When chalking, alligatoring, or thin spots appear, the UV clock
                is running. Most "leaks" we see in 15–20 year old foam roofs trace back to a coating
                that was 5+ years overdue.
              </li>
              <li>
                <strong>Bird damage:</strong> Grackles and woodpeckers peck holes through coating
                and into the foam, sometimes deep enough to expose the substrate. We patch and
                discuss deterrents during the repair.
              </li>
              <li>
                <strong>HVAC tech damage:</strong> Service techs walking on soft spots or dropping
                tools punch through the coating. Always ask techs to use walk pads.
              </li>
              <li>
                <strong>Monsoon ponding:</strong> Years of settling can create low spots that hold
                water. We can build up foam to re-slope the deck during repair so water actually
                drains.
              </li>
            </ul>
          </>
        ),
      }}
      process={{
        heading: "Our foam roof repair process",
        steps: [
          {
            title: "Free inspection and diagnosis",
            body: "We trace leaks back to their source on the roof, document conditions with photos, and give you a written estimate before any work starts.",
          },
          {
            title: "Targeted repair scope",
            body: "Honest recommendation: patch what's failing, leave what's sound. We don't upsell full recoats when a localized repair is what your roof actually needs.",
          },
          {
            title: "Refoam and recoat",
            body: "Damaged foam is cut out and replaced with matching density; coating is applied in two passes and feathered into the surrounding surface.",
          },
          {
            title: "Walkthrough and warranty",
            body: "We show you the finished repair before we leave and back the workmanship in writing.",
          },
        ],
      }}
      testimonial={{
        quote:
          "I would not allow anyone else to work on my roof beside this company. They are very knowledgeable and honest and did not recommend that I do things that were not necessary. They cleaned up the worksite perfectly. No more leaks thanks to them!",
        name: "Patricia Juarez",
        source: "Google review",
        rating: 5,
      }}
      faqs={faqs}
      ctaTitle="Got a stain on your ceiling?"
      ctaBody="Don't wait for the next monsoon storm. We'll diagnose the leak and give you a written repair estimate — at no cost."
    />
  );
}
